import { useState, useMemo } from 'react';
import {
  PrimaryButton,
  SecondaryButton,
  Prompt,
  Spinner,
  Typography,
} from '@weareopenr/component-library';
import { IExtension, IExtensionInstance } from 'src/api/extensions';
import { useExtensions, useDeleteExtension, useDeleteInstance } from 'src/hooks/useExtensions';
import { usePermissions } from 'src/hooks';
import { ExtensionCard } from './ExtensionCard';
import { InstanceModal } from './InstanceModal';
import { ConnectExtensionModal } from './ConnectExtensionModal';
import { EditAuthModal } from './EditAuthModal';
import styles from './Extensions.module.scss';

interface IExtensionsListProps {
  brandId: string;
}

type TModalState =
  | { type: 'closed' }
  | { type: 'connect' }
  | { type: 'editAuth'; extension: IExtension }
  | { type: 'addInstance'; extension: IExtension }
  | { type: 'editInstance'; extension: IExtension; instance: IExtensionInstance }
  | { type: 'deleteInstance'; extension: IExtension; instance: IExtensionInstance }
  | { type: 'deleteExtension'; extension: IExtension };

export const ExtensionsList = ({ brandId }: IExtensionsListProps) => {
  const { data: extensions, isLoading, isError } = useExtensions(brandId);
  const { canInstallExtensions } = usePermissions();
  const [modalState, setModalState] = useState<TModalState>({ type: 'closed' });

  const sortedExtensions = useMemo(
    () =>
      extensions
        ?.slice()
        .sort((a, b) => a.addon.name.toLowerCase().localeCompare(b.addon.name.toLowerCase())),
    [extensions],
  );

  const deleteExtensionMutation = useDeleteExtension(() => {
    setModalState({ type: 'closed' });
  });

  const deleteInstanceMutation = useDeleteInstance(() => {
    setModalState({ type: 'closed' });
  });

  const closeModal = () => setModalState({ type: 'closed' });

  const handleDeleteExtension = () => {
    if (modalState.type !== 'deleteExtension') return;
    deleteExtensionMutation.mutate({
      brandId,
      extensionId: modalState.extension.id,
      addonName: modalState.extension.addon.name,
    });
  };

  const handleDeleteInstance = () => {
    if (modalState.type !== 'deleteInstance') return;
    const instanceName = modalState.instance.name || 'Instance';
    deleteInstanceMutation.mutate({
      brandId,
      extensionId: modalState.extension.id,
      instanceId: modalState.instance.id,
      instanceName,
    });
  };

  if (isLoading) {
    return (
      <div className={styles['extensions-empty']}>
        <Spinner size="large" colour="rainbow" />
      </div>
    );
  }

  if (isError) {
    return (
      <div className={styles['extensions-empty']}>
        <Typography size="xlarge" weight="semi-bold">
          Failed to Load Extensions
        </Typography>
        <Typography>Something went wrong. Please refresh the page to try again.</Typography>
      </div>
    );
  }

  const isEmpty = !extensions || extensions.length === 0;

  return (
    <>
      {isEmpty ? (
        <div className={styles['extensions-empty']}>
          <div className={styles['extensions-empty-icon']} aria-hidden="true">
            🔌
          </div>
          <Typography size="xlarge" weight="semi-bold">
            No Extensions Connected
          </Typography>
          <Typography>Connect to delivery platforms, POS systems, and more.</Typography>
          {canInstallExtensions && (
            <div className={styles['connect-button']}>
              <PrimaryButton onClick={() => setModalState({ type: 'connect' })}>
                + Connect Extension
              </PrimaryButton>
            </div>
          )}
        </div>
      ) : (
        <>
          {sortedExtensions?.map((extension) => (
            <ExtensionCard
              key={extension.id}
              extension={extension}
              onEditAuth={(ext) => setModalState({ type: 'editAuth', extension: ext })}
              onAddInstance={(ext) => setModalState({ type: 'addInstance', extension: ext })}
              onEditInstance={(ext, inst) =>
                setModalState({ type: 'editInstance', extension: ext, instance: inst })
              }
              onDeleteInstance={(ext, inst) =>
                setModalState({ type: 'deleteInstance', extension: ext, instance: inst })
              }
              onDeleteExtension={(ext) =>
                setModalState({ type: 'deleteExtension', extension: ext })
              }
            />
          ))}

          {canInstallExtensions && (
            <SecondaryButton
              onClick={() => setModalState({ type: 'connect' })}
              className={styles['connect-new-button']}
            >
              + Connect New Extension
            </SecondaryButton>
          )}
        </>
      )}

      {/* Connect Extension Modal */}
      <ConnectExtensionModal
        open={modalState.type === 'connect'}
        onClose={closeModal}
        brandId={brandId}
        existingAddonIds={extensions?.map((ext) => ext.addon.id) || []}
      />

      {/* Edit Auth Modal */}
      {modalState.type === 'editAuth' && (
        <EditAuthModal
          open
          onClose={closeModal}
          brandId={brandId}
          extension={modalState.extension}
        />
      )}

      {/* Add/Edit Instance Modal */}
      {(modalState.type === 'addInstance' || modalState.type === 'editInstance') && (
        <InstanceModal
          open
          onClose={closeModal}
          brandId={brandId}
          extension={modalState.extension}
          instance={modalState.type === 'editInstance' ? modalState.instance : undefined}
        />
      )}

      {/* Delete Instance Confirmation */}
      <Prompt
        open={modalState.type === 'deleteInstance'}
        closePrompt={closeModal}
        buttonHandler={handleDeleteInstance}
        buttonText="Delete"
        isSubmitting={deleteInstanceMutation.isPending}
        promptHeading={`Delete ${
          modalState.type === 'deleteInstance' ? modalState.instance.name || 'Instance' : ''
        }?`}
      >
        This instance will be permanently deleted.
      </Prompt>

      {/* Delete Extension Confirmation */}
      <Prompt
        open={modalState.type === 'deleteExtension'}
        closePrompt={closeModal}
        buttonHandler={handleDeleteExtension}
        buttonText="Delete"
        isSubmitting={deleteExtensionMutation.isPending}
        promptHeading={`Delete ${
          modalState.type === 'deleteExtension' ? modalState.extension.addon.name : ''
        }?`}
      >
        This will permanently delete the extension and all its instances.
      </Prompt>
    </>
  );
};
